// Color palettes
const defaultColors = {
  // Primary Colors
  primary: '#00ADE6',
  primaryLight: '#E6F7FC',
  primaryHover: '#00A1D6',

  // Neutral Colors
  text: {
    primary: '#1A1A1A',
    secondary: '#6B6B6B',
    tertiary: '#999999',
    inverse: '#FFFFFF',
  },

  background: {
    primary: '#FFFFFF',
    secondary: '#FAFAFA',
    tertiary: '#F5F5F5',
    inverse: '#1A1A1A',
  },

  border: {
    default: '#E5E5E5',
    light: '#F0F0F0',
    focus: '#00ADE6',
    error: '#FF3B30',
    primary: '#E5E5E5',
  },

  // Semantic Colors
  success: '#00B341',
  successLight: '#2aff9cff',
  warning: '#FF9500',
  warningLight: '#ffc374ff',
  danger: '#FF3B30',
  info: '#007AFF',

  // Tag Colors (10% opacity backgrounds)
  tags: {
    blue: 'rgba(0, 173, 230, 0.1)',
    green: 'rgba(0, 179, 65, 0.1)',
    purple: 'rgba(128, 90, 213, 0.1)',
    orange: 'rgba(255, 149, 0, 0.1)',
    red: 'rgba(255, 59, 48, 0.1)',
    yellow: 'rgba(255, 204, 0, 0.1)',
    teal: 'rgba(90, 200, 250, 0.1)',
    pink: 'rgba(255, 45, 85, 0.1)',
    gray: 'rgba(142, 142, 147, 0.1)',
  },

  // Activity type colors
  activity: {
    call: '#ffc374ff',
    email: '#007AFF',
    meeting: '#FF9500',
    task: '#5856D6',
  },

  // Surface colors for overlays
  surface: {
    overlay: 'rgba(0, 0, 0, 0.5)',
  },
}

// Daltonism-friendly color palette (high contrast, colorblind-safe)
const daltonismColors = {
  // Primary Colors - Using blue which is universally distinguishable
  primary: '#0066CC',
  primaryLight: '#E6F0FF',
  primaryHover: '#0052A3',

  // Neutral Colors - Higher contrast
  text: {
    primary: '#000000',
    secondary: '#4D4D4D',
    tertiary: '#808080',
    inverse: '#FFFFFF',
  },

  background: {
    primary: '#FFFFFF',
    secondary: '#F5F5F5',
    tertiary: '#EBEBEB',
    inverse: '#000000',
  },

  border: {
    default: '#CCCCCC',
    light: '#E0E0E0',
    focus: '#0066CC',
    error: '#CC00CC',
    primary: '#CCCCCC',
  },

  // Semantic Colors - Avoiding red/green confusion
  success: '#0066CC', // Blue instead of green
  successLight: '#B3D9FF',
  warning: '#FF8800', // Orange is safe
  warningLight: '#FFD699',
  danger: '#CC00CC', // Magenta instead of red
  info: '#0066CC',

  // Tag Colors - High contrast, colorblind-safe palette
  tags: {
    blue: 'rgba(0, 102, 204, 0.15)',
    green: 'rgba(0, 102, 204, 0.15)', // Using blue
    purple: 'rgba(102, 51, 153, 0.15)',
    orange: 'rgba(255, 136, 0, 0.15)',
    red: 'rgba(204, 0, 204, 0.15)', // Using magenta
    yellow: 'rgba(255, 204, 0, 0.15)',
    teal: 'rgba(0, 153, 153, 0.15)',
    pink: 'rgba(255, 102, 204, 0.15)',
    gray: 'rgba(128, 128, 128, 0.15)',
  },

  // Activity type colors - Colorblind-safe
  activity: {
    call: '#FF8800',
    email: '#0066CC',
    meeting: '#FFB347',
    task: '#663399',
  },

  // Surface colors for overlays
  surface: {
    overlay: 'rgba(0, 0, 0, 0.6)',
  },
}

// Base theme configuration (shared across all themes)
const baseThemeConfig = {
  typography: {
    fontFamily: {
      sans: "'Poppins', 'Inter', 'Segoe UI', Roboto, sans-serif",
      mono: "'SF Mono', Consolas, 'Liberation Mono', Menlo, Courier, monospace",
    },
    fontSize: {
      // Label Text
      label: '11px',
      // Micro Text
      micro: '12px',
      // Small Text
      small: '13px',
      // Body Text
      body: '14px',
      // Section Headers
      section: '16px',
      // Page Titles
      title: '18px',
      // Hero Values
      hero: '28px',
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    lineHeight: {
      tight: 1.2,
      normal: 1.5,
      relaxed: 1.6,
    },
    letterSpacing: {
      normal: '0',
      uppercase: '0.5px',
    },
  },

  spacing: {
    micro: '2px',
    xsmall: '4px',
    small: '8px',
    default: '12px',
    medium: '16px',
    large: '24px',
    xlarge: '32px',
    huge: '48px',
  },

  borderRadius: {
    small: '4px', // badges, small buttons
    default: '6px', // buttons, inputs, tags
    medium: '8px', // cards, panels
    large: '10px', // single control buttons
    xlarge: '12px', // control groups, modals
    round: '24px', // FAB buttons
    circle: '50%', // avatars, round buttons
  },

  shadows: {
    sm: '0 1px 2px rgba(0,0,0,0.04)',
    default: '0 2px 8px rgba(0,0,0,0.08)',
    md: '0 1px 4px rgba(0,0,0,0.15)',
    lg: '0 4px 20px rgba(0,0,0,0.15)',
    xl: '0 4px 24px rgba(0,0,0,0.12)',
    // Special shadows
    focus: '0 0 0 3px rgba(0, 173, 230, 0.25)',
    error: '0 0 0 3px rgba(255, 59, 48, 0.25)',
    fab: '0 2px 8px rgba(0,173,230,0.3)',
  },

  transitions: {
    fast: '0.2s ease',
    default: '0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    medium: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  breakpoints: {
    mobile: '320px',
    largeMobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wideDesktop: '1200px',
    extraWide: '1400px',
  },

  zIndex: {
    elevated: 1,
    floating: 2,
    panels: 10,
    sticky: 100,
    sidebar: 900,
    header: 1000,
    dropdown: 1050,
    modal: 1100,
    popover: 1200,
    toast: 1300,
    tooltip: 1400,
  },

  layout: {
    appHeaderHeight: '64px',
    propertyHeaderHeight: '106px',
    propertyHeaderHeightMobile: 'auto',
    sidebarWidth: '200px',
    contentPadding: '32px',
    contentPaddingMobile: '16px',
    cardSpacing: '24px',
    gridGapMobile: '16px',
    gridGapDesktop: '32px',
  },

  components: {
    // Button specific styles
    button: {
      padding: {
        primary: '6px 12px',
        icon: '8px',
      },
      height: {
        default: '32px',
        icon: '32px',
        fab: '48px',
      },
      fontSize: '12px',
    },
    // Map controls
    mapControl: {
      size: '40px',
      groupRadius: '12px',
      dividerColor: 'rgba(0,0,0,0.06)',
    },
    // Property tags
    tag: {
      padding: '4px 10px',
      fontSize: '11px',
      iconSize: '12px',
    },
    // Cards
    card: {
      headerPadding: '20px 24px',
      bodyPadding: '24px',
    },
    // Toggle switches
    toggle: {
      width: '44px',
      height: '26px',
    },
    // Form elements
    input: {
      height: '32px',
      padding: '6px 12px',
    },
    // Badges
    badge: {
      padding: '4px 8px',
      fontSize: '11px',
    },
    // Charts
    radar: {
      sizeDesktop: '80px',
      sizeMobile: '60px',
    },
    // Tables
    table: {
      cellPadding: '16px',
      cellPaddingMobile: '12px',
    },
    // Progress bars
    progress: {
      height: '4px',
    },
    // Map specific
    map: {
      resizableMinWidth: '320px',
      resizableMaxWidth: '800px',
      layerPanelWidth: '280px',
    },
  },

  animations: {
    popIn: `
      @keyframes popIn {
        from {
          opacity: 0;
          transform: scale(0.8) translateY(10px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    `,
    fadeIn: `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateX(-50%) translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateX(-50%) translateY(0);
        }
      }
    `,
    slideInLeft: `
      @keyframes slideInLeft {
        from {
          transform: translateX(-100%);
        }
        to {
          transform: translateX(0);
        }
      }
    `,
    rotate: `
      @keyframes rotate {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(45deg);
        }
      }
    `,
  },
}

// Export default theme
export const theme = {
  ...baseThemeConfig,
  colors: defaultColors,
}

// Export daltonism-friendly theme
export const daltonismTheme = {
  ...baseThemeConfig,
  colors: daltonismColors,
}

export type Theme = typeof theme
export type ThemeVariant = 'default' | 'daltonism'
